import { X } from "lucide-react";
import { useId } from "react";
import {
  addSavedCategory,
  canonicalBasicName,
  datalistOptions,
  isBasicCategoryName,
  orderedCategorySuggestions,
  normalizeCategoryLabel,
} from "../lib/categories";
import { useUserSettings } from "../lib/settings-context";

const fieldClass =
  "mt-1 w-full rounded-lg border border-border bg-surface-1 px-3 py-2 text-sm text-text placeholder:text-text-muted focus:border-accent focus:ring-1 focus:ring-accent/40";

type Props = {
  id?: string;
  value: string;
  onChange: (next: string) => void;
  /** When set, one line of hint under the chips */
  hint?: string;
};

export function CategoryField({ id, value, onChange, hint }: Props) {
  const { settings, patchSettings } = useUserSettings();
  const listId = useId().replace(/:/g, "");
  const chips = orderedCategorySuggestions(settings);
  const options = datalistOptions(settings, value);

  function rememberCategory(raw: string) {
    const n = normalizeCategoryLabel(raw);
    if (!n) return;
    patchSettings({
      savedCategoryNames: addSavedCategory(settings.savedCategoryNames, n),
    });
  }

  function selectChip(label: string) {
    onChange(label);
    rememberCategory(label);
  }

  function removeChip(label: string) {
    const canonical = canonicalBasicName(label);
    if (canonical) {
      const d = settings.dismissedBasicCategories;
      if (!d.some((x) => x.toLowerCase() === canonical.toLowerCase())) {
        patchSettings({
          dismissedBasicCategories: [...d, canonical],
          savedCategoryNames: settings.savedCategoryNames.filter(
            (x) => x.toLowerCase() !== canonical.toLowerCase(),
          ),
        });
      }
    } else {
      patchSettings({
        savedCategoryNames: settings.savedCategoryNames.filter(
          (x) => x.toLowerCase() !== label.toLowerCase(),
        ),
      });
    }
  }

  return (
    <div className="space-y-2">
      <input
        id={id}
        className={fieldClass}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={() => rememberCategory(value)}
        list={listId}
        placeholder="Type or pick a category"
        autoComplete="off"
      />
      <datalist id={listId}>
        {options.map((opt) => (
          <option key={opt} value={opt} />
        ))}
      </datalist>

      {chips.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {chips.map((c) => (
            <div
              key={c}
              className="inline-flex overflow-hidden rounded-lg border border-border bg-surface-0/80 text-sm text-text shadow-sm"
            >
              <button
                type="button"
                className="px-2.5 py-1 text-left transition-colors hover:bg-surface-2/80"
                onClick={() => selectChip(c)}
              >
                {c}
              </button>
              <button
                type="button"
                title={
                  isBasicCategoryName(c)
                    ? "Hide from suggestions"
                    : "Remove saved category"
                }
                className="flex items-center border-l border-border px-1.5 text-text-muted transition-colors hover:bg-surface-2 hover:text-text"
                aria-label={
                  isBasicCategoryName(c)
                    ? `Hide ${c} from suggestions`
                    : `Remove ${c}`
                }
                onClick={(e) => {
                  e.preventDefault();
                  removeChip(c);
                }}
              >
                <X className="h-3.5 w-3.5" strokeWidth={2.25} />
              </button>
            </div>
          ))}
        </div>
      )}

      {hint ? (
        <p className="text-xs text-text-muted">{hint}</p>
      ) : null}
    </div>
  );
}
